import { Link } from 'react-router-dom';
import {
  BIORHYTHM_CYCLE_LABELS,
  BIORHYTHM_PHASE_LABELS,
  COACHING_BIORHYTHM_DISCLAIMER,
  computeBiorhythm,
} from '@mlm/shared/coaching';
import { BiorhythmCharts } from '../components/biorhythm-chart';
import { BiorhythmLucky } from '../components/biorhythm-lucky';
import { Block, ErrorNote, Loading, Meter, Screen } from '../components/primitives';
import { ProLock } from '../components/pro-lock';
import { useBiorhythmPractice, useProfile, useToday, useFeatureLocked } from '../lib/queries';

/**
 * SC-29: the member's cycles today, from their birth date alone, with the
 * day's lucky colors and stone under them. Computed on the device; reading it
 * is the day's biorhythm practice.
 */
export function BiorhythmPage() {
  const locked = useFeatureLocked('biorhythm');
  const profile = useProfile();
  const today = useToday();
  const reading =
    profile.data && today.data
      ? computeBiorhythm(profile.data.profile.birthDate, today.data.day)
      : null;
  useBiorhythmPractice(
    !locked && reading !== null,
    today.data?.practicesDone.includes('biorhythm') ?? false,
  );

  if (locked) {
    return (
      <Screen back="/align" eyebrow="Biorhythm" title="Your biorhythm today">
        <ProLock feature="biorhythm" />
      </Screen>
    );
  }
  if (profile.isPending || today.isPending) return <Loading />;
  if (profile.isError)
    return <ErrorNote error={profile.error} retry={() => void profile.refetch()} />;
  if (today.isError) return <ErrorNote error={today.error} retry={() => void today.refetch()} />;

  if (!reading) {
    return (
      <Screen back="/align" eyebrow="Biorhythm" title="Add your birth date">
        <Block
          action={
            <Link
              to="/settings"
              className="inline-flex min-h-11 items-center bg-gold px-6 font-mono text-[0.68rem] uppercase tracking-[0.14em] text-ink hover:bg-[#c29d4c]"
            >
              Add birth date
            </Link>
          }
        >
          Your biorhythm cycles begin on the day you were born, so this reading needs your birth
          date. Your birth time and place are not required.
        </Block>
      </Screen>
    );
  }

  const lead = reading.today[reading.strongest];
  return (
    <Screen back="/align" eyebrow="Biorhythm" title="Your biorhythm today" subtitle={lead.note}>
      <section className="space-y-5 border-b border-border px-5 py-6">
        {reading.cycles.map((cycle) => {
          const r = reading.today[cycle];
          return (
            <div key={cycle}>
              <Meter
                label={`${BIORHYTHM_CYCLE_LABELS[cycle]} · ${BIORHYTHM_PHASE_LABELS[r.phase]}`}
                value={r.level}
              />
              {cycle !== reading.strongest && (
                <p className="mt-2 text-sm leading-relaxed text-ink/70">{r.note}</p>
              )}
            </div>
          );
        })}
      </section>
      <BiorhythmCharts reading={reading} />
      <BiorhythmLucky reading={reading} />
      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        The numbers show where each cycle sits between its low and its high, as an index from 0 to
        100. {COACHING_BIORHYTHM_DISCLAIMER}
      </p>
    </Screen>
  );
}
